import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["emailInput", "submitButton", "submitText", "loadingText", "errorMessage"]
  static values = {
    submitUrl: String,
    // Optional override for the post-submit "check inbox" copy. Defaults to
    // the lead-magnet wording about the download link; flows that don't
    // deliver an asset (e.g. the newsletter) should pass their own message.
    checkInboxMessage: { type: String, default: "Bestätigen Sie Ihre E-Mail — der <strong class=\"text-gray-700\">Download-Link folgt per zweiter E-Mail</strong>." },
    alreadyConfirmedMessage: { type: String, default: "Der Download-Link wurde erneut an Ihre E-Mail gesendet." }
  }

  static emailProviders = {
    'gmail.com':      { name: 'Gmail',    url: 'https://mail.google.com',       color: '#EA4335', icon: 'M' },
    'googlemail.com': { name: 'Gmail',    url: 'https://mail.google.com',       color: '#EA4335', icon: 'M' },
    'gmx.de':         { name: 'GMX',      url: 'https://navigator.gmx.net/mail',color: '#1C449B', icon: 'G' },
    'gmx.net':        { name: 'GMX',      url: 'https://navigator.gmx.net/mail',color: '#1C449B', icon: 'G' },
    'gmx.at':         { name: 'GMX',      url: 'https://navigator.gmx.net/mail',color: '#1C449B', icon: 'G' },
    'web.de':         { name: 'WEB.DE',   url: 'https://web.de/email',          color: '#FFB100', icon: 'W' },
    't-online.de':    { name: 'T-Online',  url: 'https://email.t-online.de',    color: '#E20074', icon: 'T' },
    'outlook.com':    { name: 'Outlook',   url: 'https://outlook.live.com',     color: '#0078D4', icon: 'O' },
    'outlook.de':     { name: 'Outlook',   url: 'https://outlook.live.com',     color: '#0078D4', icon: 'O' },
    'hotmail.com':    { name: 'Outlook',   url: 'https://outlook.live.com',     color: '#0078D4', icon: 'O' },
    'hotmail.de':     { name: 'Outlook',   url: 'https://outlook.live.com',     color: '#0078D4', icon: 'O' },
    'live.com':       { name: 'Outlook',   url: 'https://outlook.live.com',     color: '#0078D4', icon: 'O' },
    'live.de':        { name: 'Outlook',   url: 'https://outlook.live.com',     color: '#0078D4', icon: 'O' },
    'yahoo.com':      { name: 'Yahoo',    url: 'https://mail.yahoo.com',        color: '#6001D2', icon: 'Y' },
    'yahoo.de':       { name: 'Yahoo',    url: 'https://mail.yahoo.com',        color: '#6001D2', icon: 'Y' },
    'freenet.de':     { name: 'Freenet',  url: 'https://email.freenet.de',      color: '#009EE0', icon: 'F' },
    'posteo.de':      { name: 'Posteo',   url: 'https://posteo.de/webmail',     color: '#009900', icon: 'P' },
    'icloud.com':     { name: 'iCloud',   url: 'https://www.icloud.com/mail',   color: '#333333', icon: 'i' },
    'me.com':         { name: 'iCloud',   url: 'https://www.icloud.com/mail',   color: '#333333', icon: 'i' },
  }

  static defaultProviders = ['gmail.com', 'gmx.de', 'web.de', 'outlook.com']

  connect() {
    this.injectStyles()
    this.setupScrollTrigger()
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.resendTimer) clearTimeout(this.resendTimer)
  }

  injectStyles() {
    if (document.getElementById('lead-magnet-animations')) return
    const style = document.createElement('style')
    style.id = 'lead-magnet-animations'
    style.textContent = `
      @keyframes lm-shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      @keyframes lm-icon-pulse {
        0%, 100% { box-shadow: 0 4px 6px -1px rgba(36,49,93,0.2); }
        50% { box-shadow: 0 4px 12px -1px rgba(36,49,93,0.35); }
      }
      .lm-shimmer-btn {
        position: relative;
        overflow: hidden;
      }
      .lm-shimmer-btn::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(
          110deg,
          transparent 15%,
          rgba(255,255,255,0.25) 38%,
          rgba(255,255,255,0.4) 50%,
          rgba(255,255,255,0.25) 62%,
          transparent 85%
        );
        background-size: 200% 100%;
        animation: lm-shimmer 3s ease-in-out infinite;
        animation-delay: 1.5s;
        pointer-events: none;
        border-radius: inherit;
      }
      .lm-icon-glow {
        animation: lm-icon-pulse 2.5s ease-in-out infinite;
        animation-delay: 1s;
      }
    `
    document.head.appendChild(style)
  }

  setupScrollTrigger() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.observer.disconnect()
          this.activateAttentionEffects()
        }
      })
    }, { threshold: 0.15 })

    this.observer.observe(this.element)
  }

  activateAttentionEffects() {
    // Add shimmer to all CTA buttons
    this.element.querySelectorAll('button[type="submit"]').forEach(btn => {
      btn.classList.add('lm-shimmer-btn')
    })
    // Add glow pulse to icon square
    this.element.querySelectorAll('[data-lm-icon]').forEach(icon => {
      icon.classList.add('lm-icon-glow')
    })
  }

  startResendCountdown(seconds) {
    this.resendCount = this.resendCount || 0
    const btn = document.getElementById('lm-resend-btn')
    const label = document.getElementById('lm-resend-label')
    if (!btn || !label) return

    btn.disabled = true
    let remaining = seconds

    const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

    const tick = () => {
      if (remaining <= 0) {
        label.textContent = 'Erneut senden'
        btn.disabled = false
        btn.onclick = () => this.resendEmail()
        return
      }
      label.textContent = `Erneut senden (${formatTime(remaining)})`
      remaining--
      this.resendTimer = setTimeout(tick, 1000)
    }
    tick()
  }

  async resendEmail() {
    const btn = document.getElementById('lm-resend-btn')
    const label = document.getElementById('lm-resend-label')
    if (!btn || !label) return

    btn.disabled = true
    label.textContent = 'Wird gesendet...'

    let ok = false
    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
        },
        body: JSON.stringify({ email: this.submittedEmail })
      })
      ok = response.ok
    } catch (_) { /* network error */ }

    if (!ok) {
      label.textContent = 'Erneut senden'
      btn.disabled = false
      return
    }

    this.resendCount = (this.resendCount || 0) + 1

    if (this.resendCount >= 3) {
      label.textContent = 'E-Mail wurde erneut gesendet'
      btn.disabled = true
      btn.classList.remove('underline', 'decoration-dotted')
      return
    }

    label.textContent = 'E-Mail wurde erneut gesendet'
    setTimeout(() => this.startResendCountdown(60), 1500)
  }

  async submit(event) {
    event.preventDefault()

    const form = event.target
    const email = form.querySelector('input[type="email"]').value.trim()
    if (!email) return

    this.activeForm = form
    this.showLoading()
    this.hideError()

    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
        },
        body: JSON.stringify({ email })
      })

      if (response.ok) {
        const data = await response.json()
        this.submittedEmail = email
        if (data.status === "already_confirmed") {
          this.transitionTo(this.alreadyConfirmedHTML())
        } else {
          this.transitionTo(this.checkInboxHTML(), () => this.startResendCountdown(120))
        }
      } else {
        let errorMsg = "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut."
        try {
          const errorData = await response.json()
          if (errorData.error) errorMsg = errorData.error
        } catch (_) { /* non-JSON response */ }
        this.showError(errorMsg)
        this.resetLoading()
      }
    } catch (error) {
      this.showError("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.")
      this.resetLoading()
    }
  }

  transitionTo(html, onComplete) {
    const container = this.element
    const currentHeight = container.offsetHeight
    container.style.height = `${currentHeight}px`
    container.style.overflow = "hidden"
    container.style.transition = "opacity 0.3s ease, transform 0.3s ease"
    container.style.opacity = "0"
    container.style.transform = "scale(0.98)"

    setTimeout(() => {
      container.innerHTML = html
      container.style.opacity = "1"
      container.style.transform = "scale(1)"

      // Measure the new content's natural height, then animate from the
      // locked height to it. Without this the container stays clamped to
      // the old form height and taller post-submit states (e.g. the
      // "Fast geschafft!" inbox view) get clipped at the top.
      container.style.height = "auto"
      const newHeight = container.offsetHeight
      container.style.height = `${currentHeight}px`
      void container.offsetHeight // force reflow before animating
      container.style.transition = "opacity 0.3s ease, transform 0.3s ease, height 0.4s ease"
      container.style.height = `${newHeight}px`

      setTimeout(() => {
        container.style.height = ""
        container.style.overflow = ""
        container.style.transition = ""
      }, 450)

      // Stagger-animate the list items
      const animItems = container.querySelectorAll("[data-animate]")
      animItems.forEach((el, i) => {
        el.style.opacity = "0"
        el.style.transform = "translateY(8px)"
        el.style.transition = `opacity 0.4s ease ${i * 0.1 + 0.2}s, transform 0.4s ease ${i * 0.1 + 0.2}s`
        requestAnimationFrame(() => {
          el.style.opacity = "1"
          el.style.transform = "translateY(0)"
        })
      })

      if (onComplete) onComplete()
    }, 300)
  }

  showLoading() {
    const btn = this.activeForm.querySelector('button[type="submit"]')
    btn.style.minHeight = `${btn.offsetHeight}px`
    btn.disabled = true
    btn.querySelector('[data-lead-magnet-form-target="submitText"]').classList.add("hidden")
    btn.querySelector('[data-lead-magnet-form-target="loadingText"]').classList.remove("hidden")
  }

  resetLoading() {
    const btn = this.activeForm.querySelector('button[type="submit"]')
    btn.disabled = false
    btn.querySelector('[data-lead-magnet-form-target="submitText"]').classList.remove("hidden")
    btn.querySelector('[data-lead-magnet-form-target="loadingText"]').classList.add("hidden")
  }

  showError(message) {
    const errorEl = this.activeForm.querySelector('[data-lead-magnet-form-target="errorMessage"]')
    errorEl.textContent = message
    errorEl.classList.remove("hidden")
  }

  hideError() {
    const errorEl = this.activeForm.querySelector('[data-lead-magnet-form-target="errorMessage"]')
    if (errorEl) errorEl.classList.add("hidden")
  }

  getProviderButtons() {
    const domain = (this.submittedEmail || "").split("@")[1]?.toLowerCase()
    const providers = this.constructor.emailProviders
    const matched = providers[domain]

    if (matched) {
      return this.providerButtonHTML(matched, true)
    }

    // Unknown provider — show top German providers
    return this.constructor.defaultProviders.map(key => {
      return this.providerButtonHTML(providers[key], false)
    }).join('') + `
      <span class="text-[11px] text-gray-400 block mt-1">Oder öffnen Sie Ihr E-Mail-Programm direkt</span>
    `
  }

  providerButtonHTML(provider, primary) {
    if (primary) {
      return `
        <a href="${provider.url}" target="_blank" rel="noopener noreferrer"
           class="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold text-white transition hover:opacity-90"
           style="background-color: ${provider.color};" data-animate>
          <span class="flex h-5 w-5 items-center justify-center rounded text-[11px] font-bold bg-white/20">${provider.icon}</span>
          ${provider.name} öffnen
        </a>
      `
    }
    return `
      <a href="${provider.url}" target="_blank" rel="noopener noreferrer"
         class="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50" data-animate>
        <span class="flex h-4 w-4 items-center justify-center rounded text-[9px] font-bold text-white" style="background-color: ${provider.color};">${provider.icon}</span>
        ${provider.name}
      </a>
    `
  }

  checkInboxHTML() {
    const providerButtons = this.getProviderButtons()
    return `
      <div class="flex items-center justify-center h-full px-6 py-4 sm:px-10 sm:py-4 text-center"
           style="background: radial-gradient(ellipse at 50% 30%, rgba(36,49,93,0.06) 0%, transparent 60%), white;">
        <div>
          <div class="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 ring-4 ring-blue-100/50" data-animate>
            <svg class="h-5 w-5 text-[#24315D]" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>

          <h2 class="text-lg font-bold text-gray-900 mb-1" data-animate>
            Fast geschafft!
          </h2>
          <p class="text-sm text-gray-500 max-w-sm mx-auto mb-4" data-animate>
            ${this.checkInboxMessageValue}
          </p>

          <div class="flex flex-wrap items-center justify-center gap-2 mb-3" data-animate>
            ${providerButtons}
          </div>

          <div class="mt-1" data-animate>
            <p class="text-xs text-gray-400 mb-1.5">Keine E-Mail? Prüfen Sie Ihren Spam-Ordner.</p>
            <button id="lm-resend-btn" disabled
                    class="text-xs text-gray-400 underline decoration-dotted underline-offset-2 hover:text-gray-600 disabled:no-underline disabled:cursor-default disabled:hover:text-gray-400 transition">
              <span id="lm-resend-label">Erneut senden (2:00)</span>
            </button>
          </div>
        </div>
      </div>
    `
  }

  alreadyConfirmedHTML() {
    return `
      <div class="flex items-center justify-center h-full px-6 py-4 sm:px-10 sm:py-4 text-center"
           style="background: radial-gradient(ellipse at 50% 30%, rgba(36,49,93,0.06) 0%, transparent 60%), white;">
        <div>
          <div class="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-blue-50 ring-4 ring-blue-100/50" data-animate>
            <svg class="h-5 w-5 text-[#24315D]" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 class="text-lg font-bold text-gray-900 mb-1" data-animate>
            Bereits bestätigt!
          </h2>
          <p class="text-sm text-gray-500 max-w-md mx-auto" data-animate>
            ${this.alreadyConfirmedMessageValue}
          </p>
        </div>
      </div>
    `
  }
}
